import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import {
  getStationLabelForRole,
  getWorkshopPhaseForUser,
  isLotUrgent,
  isLotOverdue,
} from '../../../lib/productionTrackingQueues';

const STATUS_LABELS = {
  pending: { label: 'En attente', className: 'bg-muted text-muted-foreground' },
  in_progress: { label: 'En cours', className: 'bg-primary/10 text-primary' },
  quality_check: { label: 'Contrôle qualité', className: 'bg-warning/10 text-warning' },
  packaging: { label: 'Emballage', className: 'bg-success/10 text-success' },
  completed: { label: 'Terminé', className: 'bg-success/10 text-success' },
  delayed: { label: 'Retardé', className: 'bg-destructive/10 text-destructive' },
};

function formatDate(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
  });
}

const ProductionLotRow = ({
  batch,
  userRole,
  canOpenWorkshop = true,
  showManagerActions = false,
  onOpenDetails,
  segment,
  assignedToMe = false,
  compact = false,
  showStatus = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const statusCfg = STATUS_LABELS[batch?.status] || STATUS_LABELS.pending;
  const urgent = isLotUrgent(batch);
  const overdue = isLotOverdue(batch);
  const workshopPhase = getWorkshopPhaseForUser(batch, userRole);
  const phaseLabel = workshopPhase?.phase_name || workshopPhase?.name;
  const stationLabel = getStationLabelForRole(userRole);
  const atStation = workshopPhase?.status === 'in_progress';

  const handleOpenWorkshop = () => {
    if (!batch?.id) return;
    navigate(`/atelier-lot?batch=${encodeURIComponent(batch.id)}`);
  };

  if (compact) {
    return (
      <article
        className={`rounded-xl border bg-card p-4 transition-[box-shadow,border-color] duration-200 hover:shadow-md ${
          urgent || overdue ? 'border-warning/50' : 'border-border'
        }`}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-foreground truncate">
                {batch.caseNumber || batch.batchNumber}
              </span>
              {batch.caseNumber && batch.batchNumber && batch.caseNumber !== batch.batchNumber && (
                <span className="text-xs text-muted-foreground truncate">
                  {batch.batchNumber}
                </span>
              )}
              {showStatus && (
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusCfg.className}`}
                >
                  {statusCfg.label}
                </span>
              )}
              {urgent && (
                <span className="inline-flex items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warning">
                  <Icon name="AlertTriangle" size={10} aria-hidden />
                  {t('productionTracking.lot.urgent')}
                </span>
              )}
              {overdue && !urgent && (
                <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-destructive">
                  <Icon name="Clock" size={10} aria-hidden />
                  {t('productionTracking.lot.overdue')}
                </span>
              )}
            </div>
            <p className="mt-1 truncate text-sm text-muted-foreground">
              {batch.patientName || '—'}
            </p>
          </div>
          {canOpenWorkshop && batch?.id && (
            <Button
              type="button"
              className="min-h-[44px] shrink-0 font-semibold sm:min-w-[170px]"
              iconName="Factory"
              onClick={handleOpenWorkshop}
            >
              {t('productionTracking.lot.openWorkshop')}
            </Button>
          )}
        </div>
      </article>
    );
  }

  return (
    <article
      className={`rounded-xl border bg-card p-4 transition-[box-shadow,border-color] duration-200 hover:shadow-md ${
        urgent || overdue
          ? 'border-warning/50'
          : atStation
            ? 'border-primary/35'
            : 'border-border'
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-semibold text-foreground truncate">
              {batch.batchNumber}
            </span>
            {atStation && (
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                {t('productionTracking.lot.atStation')}
              </span>
            )}
            {assignedToMe && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-secondary/15 text-secondary-foreground">
                {t('productionTracking.lot.assignedToMe')}
              </span>
            )}
            {urgent && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-warning/15 text-warning">
                <Icon name="AlertTriangle" size={10} aria-hidden />
                {t('productionTracking.lot.urgent')}
              </span>
            )}
            {overdue && !urgent && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">
                <Icon name="Clock" size={10} aria-hidden />
                {t('productionTracking.lot.overdue')}
              </span>
            )}
            {segment === 'waiting' && !atStation && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                {t('productionTracking.segments.waiting')}
              </span>
            )}
          </div>
          <p className="text-sm text-foreground truncate">
            {batch.patientName}
            {batch.caseNumber ? (
              <span className="text-muted-foreground"> · {batch.caseNumber}</span>
            ) : null}
          </p>
          {batch.dentistName && batch.dentistName !== '—' && (
            <p className="text-xs text-muted-foreground truncate">{batch.dentistName}</p>
          )}
        </div>
        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${statusCfg.className}`}
        >
          {statusCfg.label}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-3 text-xs text-muted-foreground">
        {phaseLabel && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary/5 text-primary">
            <Icon name="Layers" size={12} />
            {phaseLabel}
          </span>
        )}
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted/60">
          <Icon name="User" size={12} />
          {stationLabel}
        </span>
        <span className="inline-flex items-center gap-1">
          <Icon name="Calendar" size={12} />
          {t('productionTracking.lot.deadline')} {formatDate(batch.deadline)}
        </span>
        {batch.assignedMachine && (
          <span className="inline-flex items-center gap-1">
            <Icon name="Cog" size={12} />
            {batch.assignedMachine}
          </span>
        )}
      </div>

      {typeof batch.progress === 'number' && batch.status === 'in_progress' && (
        <div className="h-1.5 rounded-full bg-muted mb-3 overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all"
            style={{ width: `${Math.min(100, batch.progress)}%` }}
          />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        {canOpenWorkshop && batch?.id && (
          <Button
            type="button"
            className="flex-1 min-h-[44px] font-semibold"
            iconName="Factory"
            onClick={handleOpenWorkshop}
          >
            {t('productionTracking.lot.openWorkshop')}
          </Button>
        )}
        {showManagerActions && typeof onOpenDetails === 'function' && (
          <Button
            type="button"
            variant="outline"
            className="min-h-[44px] sm:max-w-[140px]"
            iconName="Settings2"
            onClick={() => onOpenDetails(batch)}
          >
            {t('productionTracking.lot.details')}
          </Button>
        )}
      </div>
    </article>
  );
};

export default ProductionLotRow;
